export interface IAppRoute {
    path: string
    getLink: (params?: any) => string
    title: () => string
}

export type AppRoute = {
    title: () => string
    path: string
    getLink: () => string
}
export type AppDynamicRoute<T1, T2 = unknown, T3 = unknown> = {
    path: string
    title: () => string
    getLink: (arg1?: T1, arg2?: T2, arg3?: T3) => string
}

export type GuestRoutesDictBase = {
    landing: AppRoute
    login: AppRoute
    passwordRecover: AppRoute & {
        children: {
            verify: AppDynamicRoute<string>
        }
    }
}

export type AdminRoutesDictBase = {
    system: {
        version: AppDynamicRoute<string>
        status: AppRoute
        events: AppRoute
    }
    dashboard: AppRoute
    companies: AppRoute & {
        children: {
            new: AppRoute
            edit: AppDynamicRoute<number>
        }
    }
    suppliers: AppRoute & {
        children: {
            new: AppRoute
            edit: AppDynamicRoute<number>
        }
    }
    users: AppRoute & {
        children: {
            new: AppRoute
            edit: AppDynamicRoute<number>
        }
    }
    paymentRequest: AppRoute & {
        children: {
            new: AppRoute
            edit: AppDynamicRoute<number>
        }
    }
    invoices: AppRoute & {
        children: {
            new: AppRoute
            detail: AppDynamicRoute<number>
        }
    }
    invoicePreview: AppDynamicRoute<number>
    archive: AppRoute & {
        children: {
            upload: AppRoute
        }
    }
    budgets: AppRoute
    companyDirectory: AppRoute
    utils: {
        invoicesExport: AppRoute
    }
}

export const AppGuestRoutesBase: GuestRoutesDictBase = {
    landing: {
        path: "/",
        title: () => "Homepage",
        getLink: () => "/",
    },
    login: {
        path: "login",
        title: () => "Login",
        getLink: () => "/login",
    },
    passwordRecover: {
        path: "password-recover",
        title: () => "Password recover",
        getLink: () => "/password-recover",
        children: {
            verify: {
                path: ":token",
                title: () => "Password verify",
                getLink: (token?: string) => `/password-recover/${token}`,
            },
        },
    },
}

export const AppAdminRoutesBase: AdminRoutesDictBase = {
    system: {
        version: {
            path: "version/:version",
            title: () => "Verze aplikace",
            getLink: (version?: string) => `/version/${version}`,
        },
        status: {
            path: "status",
            title: () => "Status aplikace",
            getLink: () => `/status`,
        },
        events: {
            path: "events",
            title: () => "Události",
            getLink: () => `/events`,
        },
    },
    dashboard: {
        path: "/",
        title: () => "Dashboard",
        getLink: () => "/",
    },
    companies: {
        path: "companies",
        title: () => "Companies",
        getLink: () => "/companies",
        children: {
            new: {
                path: "new",
                title: () => "New company",
                getLink: () => "/companies/new",
            },
            edit: {
                path: ":companyId",
                title: () => "Edit company",
                getLink: (id?: number) => `/companies/${id}`,
            },
        },
    },
    suppliers: {
        path: "suppliers",
        title: () => "Suppliers",
        getLink: () => "/suppliers",
        children: {
            new: {
                path: "new",
                title: () => "New supplier",
                getLink: () => "/suppliers/new",
            },
            edit: {
                path: ":supplierId",
                title: () => "Edit supplier",
                getLink: (id?: number) => `/suppliers/${id}`,
            },
        },
    },
    users: {
        path: "users",
        title: () => "Users",
        getLink: () => "/users",
        children: {
            new: {
                path: "new",
                title: () => "New user",
                getLink: () => "/users/new",
            },
            edit: {
                path: ":userId",
                title: () => "Edit user",
                getLink: (id?: number) => `/users/${id}`,
            },
        },
    },
    paymentRequest: {
        path: "payment-requests",
        title: () => "Payment requests",
        getLink: () => "/payment-requests",
        children: {
            new: {
                path: "new",
                title: () => "New payment request",
                getLink: () => "/payment-requests/new",
            },
            edit: {
                path: ":requestId",
                title: () => "Edit payment request",
                getLink: (id?: number) => `/payment-requests/${id}`,
            },
        },
    },
    invoices: {
        path: "invoices",
        title: () => "Invoices",
        getLink: () => "/invoices",
        children: {
            new: {
                path: "new",
                title: () => "New invoice",
                getLink: () => "/invoices/new",
            },
            detail: {
                path: ":invoiceId",
                title: () => "Invoice detail",
                getLink: (id?: number) => `/invoices/${id}`,
            },
        },
    },
    invoicePreview: {
        path: "invoice/:invoiceId/preview",
        title: () => "Invoice preview",
        getLink: (id?: number) => `/invoice/${id}/preview`,
    },
    archive: {
        path: "archive",
        title: () => "Archive",
        getLink: () => "/archive",
        children: {
            upload: {
                path: "upload",
                title: () => "Upload",
                getLink: () => "/archive/upload",
            },
        },
    },
    budgets: {
        path: "budgets",
        title: () => "Budgets",
        getLink: () => "/budgets",
    },
    companyDirectory: {
        path: "directory",
        title: () => "Company directory",
        getLink: () => "/directory",
    },
    utils: {
        invoicesExport: {
            path: "invoices-export",
            title: () => "Invoices export",
            getLink: () => "/invoices-export",
        },
    },
}
